import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { ApiError } from '@app/api/ApiError';
import { GATEWAY_LIST_TIMEOUT_MS } from '@app/api/client';
import { shellKeys } from '@app/api/hooks/shell';
import { del, get, post, put, withPath, type ApiBody, type ApiData } from '@app/api/typed';
import type { components } from '@app/api/types';
import { PAGE_SIZE } from '@zarya/ui';

/**
 * Hooks de T6/T7 (Resources). O contrato está congelado: as leituras são
 * `listResources`, `getResourcesSummary`, `getResource` e `getResourceImpact`;
 * as escritas, `createResource`, `updateResource`, `reimportResource`,
 * `deleteResource` e `deleteResourceTool`.
 */

export type ResourcesPageData = ApiData<'/api/v1/resources'>;
export type Resource = components['schemas']['Resource'];
export type ResourceTool = components['schemas']['Tool'];
export type ResourceSummary = ApiData<'/api/v1/resources/summary'>;
export type KindResource = components['schemas']['Resource']['kind'];
export type RiscoExibido = components['schemas']['RiscoExibido'];
export type Risco = components['schemas']['Risco'];
export type ResourceCreate = ApiBody<'/api/v1/resources', 'post'>;
export type ResourceUpdate = ApiBody<'/api/v1/resources/{id}', 'put'>;
export type ResourceImpact = ApiData<'/api/v1/resources/{id}/impact'>;
export type ReductionResult = ApiData<'/api/v1/resources/{id}', 'delete'>;
export type ImportPreview = ApiData<'/api/v1/resources/import/preview', 'post'>;
export type ImportPreviewItem = components['schemas']['ImportPreviewItem'];
export type ImportResult = ApiData<'/api/v1/resources/import/confirm', 'post'>;
/** aim-uig.40 §5.6: as APIs do gateway fora de api/, mcp/ e a2a/, e o modo em vigor. */
export type UngovernedResources = ApiData<'/api/v1/resources/ungoverned'>;
export type UngovernedApi = components['schemas']['UngovernedApi'];
export type InfraConfig = ApiData<'/api/v1/infra/config'>;
export type ApplicationUser = components['schemas']['User'];
/** Fase 2 (aim-co1.3): o bloco que só um recurso agente tem. */
export type ResourceAgent = components['schemas']['ResourceAgent'];
export type AgentState = components['schemas']['AgentState'];
export type AgentScope = ResourceAgent['scope'];
export type AgentInvoker = components['schemas']['AgentInvoker'];
export type AgentSyncResult = ApiData<'/api/v1/resources/agents/sync', 'post'>;

/**
 * Ordenações que `listResources` aceita (`sort`, enum fechado do contrato).
 * A tela não pode oferecer nenhuma outra: valor fora deste conjunto o servidor
 * recusa, e a coluna que o oferecesse mentiria — a mesma regra de T4.
 */
export const SORTINGS = ['name', '-name', 'gateway_id', '-gateway_id', 'kind'] as const;
export type SortingResources = (typeof SORTINGS)[number];

/** Filtros de T6 (FR-037): busca, tipo, dono e a pílula "com tools sem risco". */
export interface ResourcesFilterValues {
  q: string;
  kind: KindResource | null;
  owner: string | null;
  semRisco: boolean;
  /**
   * Slug do site (P1). `null` = todos.
   *
   * Vai ao SERVIDOR, e é por isso que ele está no `queryKey`: a lista pagina, e um
   * recorte feito depois de ler mostraria as linhas certas com o `total` errado.
   */
  site: string | null;
  /**
   * Ligação do agente (fase 2): `internal` · `external`. Vai ao servidor (`scope`) pela mesma razão do site — a
   * lista pagina. Escolher um deixa de fora os MCP servers e as APIs, que não têm ligação.
   */
  scope: AgentScope | null;
  /** `null` = a ordem padrão do servidor (`nome`), que não vai na query. */
  sort: SortingResources | null;
  page: number;
}

export const FILTERS_INICIAIS: ResourcesFilterValues = {
  q: '',
  kind: null,
  owner: null,
  semRisco: false,
  site: null,
  scope: null,
  sort: null,
  page: 1,
};

export function hasFilter(filters: ResourcesFilterValues): boolean {
  return (
    filters.q.trim() !== '' ||
    filters.kind !== null ||
    filters.owner !== null ||
    filters.site !== null ||
    filters.scope !== null ||
    filters.semRisco
  );
}

export const resourcesKeys = {
  todos: ['resources'] as const,
  lista: (filters: ResourcesFilterValues) =>
    [
      'resources',
      'lista',
      filters.q,
      filters.kind,
      filters.owner,
      filters.semRisco,
      filters.site,
      filters.scope,
      filters.sort,
      filters.page,
    ] as const,
  resumo: ['resources', 'resumo'] as const,
  ambientes: ['resources', 'ambientes'] as const,
  // `usuariosParaDono` saiu daqui em 28/08 — a chave agora é `donoKeys.usuarios`
  // em `@app/shared/dono`, uma só para o dono do resource (T7) e o da identidade (T5).
  item: (id: string) => ['resources', 'item', id] as const,
  impact: (id: string, toolId: string | null) =>
    ['resources', 'impact', id, toolId ?? 'resource'] as const,
  ungoverned: (siteId: string | null) => ['resources', 'ungoverned', siteId ?? 'default'] as const,
};

/** Página do catálogo (FR-037, FR-128). */
export function useResources(
  filters: ResourcesFilterValues,
): UseQueryResult<ResourcesPageData, ApiError> {
  return useQuery<ResourcesPageData, ApiError>({
    queryKey: resourcesKeys.lista(filters),
    queryFn: () =>
      get('/api/v1/resources', {
        params: {
          q: filters.q.trim() || undefined,
          kind: filters.kind ?? undefined,
          owner: filters.owner ?? undefined,
          without_risk: filters.semRisco ? true : undefined,
          site: filters.site ?? undefined,
          scope: filters.scope ?? undefined,
          sort: filters.sort ?? undefined,
          page: filters.page,
          size: PAGE_SIZE,
        },
      }),
    // Digitar na busca não deve piscar o esqueleto a cada tecla (FR-126).
    placeholderData: (previous) => previous,
  });
}

/**
 * Resumo do cabeçalho e do card **Agentes como destino** (FR-037), mais o
 * `apim_pronto` que decide o estado de **Importar do APIM** (FR-028).
 */
export function useResourcesSummary(): UseQueryResult<ResourceSummary, ApiError> {
  return useQuery<ResourceSummary, ApiError>({
    queryKey: resourcesKeys.resumo,
    queryFn: () => get('/api/v1/resources/summary'),
  });
}

/**
 * Ambientes já usados no catálogo (ajustes-front #4): o campo Ambiente de T7
 * sugere o que os outros resources usam e aceita valor novo. Derivado de
 * `listResources` — o resumo não traz ambientes e o contrato está congelado —
 * numa página só, do tamanho máximo do contrato (200; a PoC tem dezenas).
 */
export function useResourceEnvironments(ativo: boolean): UseQueryResult<string[], ApiError> {
  return useQuery<string[], ApiError>({
    queryKey: resourcesKeys.ambientes,
    enabled: ativo,
    queryFn: async () => {
      const page = await get('/api/v1/resources', { params: { page: 1, size: 200 } });
      const usados = ((page.items ?? []) as Resource[])
        .map((item) => (item.environment ?? '').trim())
        .filter(Boolean);
      return [...new Set(usados)].sort((a, b) => a.localeCompare(b, 'pt-BR'));
    },
  });
}

// O hook dos usuários do campo **Dono** mudou-se para `@app/shared/dono` em
// 28/08, quando a identidade (T5) também ganhou dono: duas telas, uma consulta e
// uma chave de cache só. Reexportado aqui por compatibilidade de importação.
export { useUsersForOwner } from '@app/shared/owner';

/** Depois de escrever, a lista, o resumo e os contadores do menu saem do lugar. */
function useInvalidarCatalog() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: resourcesKeys.todos });
    void queryClient.invalidateQueries({ queryKey: shellKeys.shell });
  };
}

/** Resource do drawer T7 (FR-029). `null` = drawer de criação. */
export function useResource(id: string | null): UseQueryResult<Resource, ApiError> {
  return useQuery<Resource, ApiError>({
    queryKey: resourcesKeys.item(id ?? ''),
    queryFn: () =>
      get(withPath('/api/v1/resources/{id}', { id: id ?? '' }) as '/api/v1/resources/{id}'),
    enabled: Boolean(id),
  });
}

/**
 * **Criar resource manual** (FR-034): local, nunca vai ao catálogo do
 * middleware — é o que a ausência de `catalog.apply` aqui significa.
 */
export function useCreateResource(): UseMutationResult<Resource, ApiError, ResourceCreate> {
  const invalidar = useInvalidarCatalog();
  return useMutation<Resource, ApiError, ResourceCreate>({
    mutationFn: (body) => post('/api/v1/resources', { body }),
    onSuccess: invalidar,
  });
}

/**
 * **Salvar** (FR-033): resource do gateway publica o catálogo inteiro no
 * executor e só grava depois do 2xx. A resposta é o resource efetivo — semeamos
 * o cache com ela para a tela mostrar o que ficou gravado, não o que foi enviado.
 */
export function useUpdateResource(
  id: string,
): UseMutationResult<Resource, ApiError, ResourceUpdate> {
  const queryClient = useQueryClient();
  const invalidar = useInvalidarCatalog();
  return useMutation<Resource, ApiError, ResourceUpdate>({
    mutationFn: (body) =>
      put(withPath('/api/v1/resources/{id}', { id }) as '/api/v1/resources/{id}', { body }),
    onSuccess: (resource) => {
      queryClient.setQueryData(resourcesKeys.item(id), resource);
      invalidar();
    },
  });
}

/** **Reimportar do gateway** — atualiza o inventário sem apagar dono e risco. */
export function useReimportResource(id: string): UseMutationResult<Resource, ApiError, void> {
  const queryClient = useQueryClient();
  const invalidar = useInvalidarCatalog();
  return useMutation<Resource, ApiError, void>({
    mutationFn: () =>
      post(
        withPath('/api/v1/resources/{id}/reimport', {
          id,
        }) as '/api/v1/resources/{id}/reimport',
        { timeoutMs: GATEWAY_LIST_TIMEOUT_MS },
      ),
    onSuccess: (resource) => {
      queryClient.setQueryData(resourcesKeys.item(id), resource);
      invalidar();
    },
  });
}

/**
 * Impacto da exclusão (FR-035, FR-130) — lido só quando a confirmação abre.
 * `toolId` presente = impacto de remover só aquela tool.
 */
export function useResourceImpact(
  id: string,
  toolId: string | null,
  habilitado: boolean,
): UseQueryResult<ResourceImpact, ApiError> {
  return useQuery<ResourceImpact, ApiError>({
    queryKey: resourcesKeys.impact(id, toolId),
    queryFn: () =>
      get(withPath('/api/v1/resources/{id}/impact', { id }) as '/api/v1/resources/{id}/impact', {
        params: toolId ? { tool_id: toolId } : undefined,
      }),
    enabled: habilitado,
    gcTime: 0,
  });
}

/**
 * **Reler o card** · **Conferir de novo** · **Converter em agente externo** (fase 2): as três são a mesma leitura
 * — o gateway do site é a declaração (decisão 21), e o produto só relê o que o APIM diz agora.
 */
export function useSyncAgents(): UseMutationResult<AgentSyncResult, ApiError, string | null> {
  const invalidar = useInvalidarCatalog();
  return useMutation<AgentSyncResult, ApiError, string | null>({
    mutationFn: (siteId) =>
      post('/api/v1/resources/agents/sync', {
        body: { site_id: siteId ?? null },
        timeoutMs: GATEWAY_LIST_TIMEOUT_MS,
      }),
    onSuccess: invalidar,
  });
}

/**
 * **Religar a uma identidade** (decisão 23, D8): o produto relê o gateway e confere que a API do agente é atendida
 * pelos pods da identidade escolhida, e só dela. Quem chamava volta a alcançar o agente.
 */
export function useRelinkAgent(id: string): UseMutationResult<Resource, ApiError, string> {
  const queryClient = useQueryClient();
  const invalidar = useInvalidarCatalog();
  return useMutation<Resource, ApiError, string>({
    mutationFn: (identityId) =>
      post(withPath('/api/v1/resources/{id}/relink', { id }) as '/api/v1/resources/{id}/relink', {
        body: { identity_id: identityId },
        timeoutMs: GATEWAY_LIST_TIMEOUT_MS,
      }),
    onSuccess: (resource) => {
      queryClient.setQueryData(resourcesKeys.item(id), resource);
      invalidar();
    },
  });
}

/** **Excluir resource** (FR-036): redução automática em uma única operação. */
export function useDeleteResource(id: string): UseMutationResult<ReductionResult, ApiError, void> {
  const queryClient = useQueryClient();
  const invalidar = useInvalidarCatalog();
  return useMutation<ReductionResult, ApiError, void>({
    mutationFn: () => del(withPath('/api/v1/resources/{id}', { id }) as '/api/v1/resources/{id}'),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: resourcesKeys.item(id) });
      invalidar();
    },
  });
}

/** **Remover tool** — mesma redução automática da exclusão de resource. */
export function useDeleteResourceTool(
  id: string,
): UseMutationResult<ReductionResult, ApiError, string> {
  const queryClient = useQueryClient();
  const invalidar = useInvalidarCatalog();
  return useMutation<ReductionResult, ApiError, string>({
    mutationFn: (toolId) =>
      del(
        withPath('/api/v1/resources/{id}/tools/{tool_id}', {
          id,
          tool_id: toolId,
        }) as '/api/v1/resources/{id}/tools/{tool_id}',
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: resourcesKeys.item(id) });
      invalidar();
    },
  });
}

/**
 * **Importar do APIM** (T8, FR-026): a pré-visualização é uma leitura do gateway
 * feita **agora**, então é `useMutation` — abrir o drawer de novo relê, e não
 * serve um resultado velho de cache.
 */
export function useResourceImportPreview(): UseMutationResult<
  ImportPreview,
  ApiError,
  string | null
> {
  return useMutation<ImportPreview, ApiError, string | null>({
    // O SITE VAI NA PRÉVIA TAMBÉM, e não só no confirm (P1-d). É dele que sai o
    // gateway lido: pedir a prévia sem site e confirmar com site produziria uma
    // classificação sobre um cluster e uma gravação noutro — «já existe» e «sumiu
    // do gateway» sobre recursos que estão certos no lugar deles.
    //
    // Nulo é «não escolhi» e vai sem corpo: é o caminho de todo tenant single-site,
    // onde a rota nunca teve corpo nenhum.
    mutationFn: (siteId) =>
      post(
        '/api/v1/resources/import/preview',
        siteId
          ? { body: { site_id: siteId }, timeoutMs: GATEWAY_LIST_TIMEOUT_MS }
          : { timeoutMs: GATEWAY_LIST_TIMEOUT_MS },
      ),
  });
}

/**
 * A lista **Fora do padrão** da prévia de T8 (aim-uig.40 §5.6): as APIs do gateway que
 * a policy global não governa, e em que modo está a chamada de um agente a elas.
 *
 * Leitura do MESMO site da prévia, feita agora, como ela: `gcTime: 0` para reabrir o
 * drawer não servir a lista de antes. Sem `retry` — um executor anterior à policy global
 * responde `502 rota_ausente`, e tentar de novo não muda isso.
 */
export function useUngovernedResources(
  siteId: string | null,
): UseQueryResult<UngovernedResources, ApiError> {
  return useQuery<UngovernedResources, ApiError>({
    queryKey: resourcesKeys.ungoverned(siteId),
    queryFn: () =>
      get(
        '/api/v1/resources/ungoverned',
        siteId
          ? { params: { site_id: siteId }, timeoutMs: GATEWAY_LIST_TIMEOUT_MS }
          : { timeoutMs: GATEWAY_LIST_TIMEOUT_MS },
      ),
    gcTime: 0,
    retry: false,
  });
}

/** Variáveis do confirm: os escolhidos e o site que os recebe (P1-d). */
export interface ConfirmImportVars {
  gatewayIds: string[];
  /** `null` = o site `default`, como na prévia. */
  siteId: string | null;
}

/** **Confirmar importação** (FR-027). */
export function useConfirmResourceImport(): UseMutationResult<
  ImportResult,
  ApiError,
  ConfirmImportVars
> {
  const invalidar = useInvalidarCatalog();
  return useMutation<ImportResult, ApiError, ConfirmImportVars>({
    mutationFn: ({ gatewayIds, siteId }) =>
      post('/api/v1/resources/import/confirm', {
        body: siteId ? { gateway_ids: gatewayIds, site_id: siteId } : { gateway_ids: gatewayIds },
        timeoutMs: GATEWAY_LIST_TIMEOUT_MS,
      }),
    onSuccess: invalidar,
  });
}

/**
 * Configuração da infraestrutura — o grupo `apim` dá o nome do APIM que o
 * cabeçalho de T8 mostra. Mesma chave de cache que T20 usa.
 */
export function useInfraConfig(): UseQueryResult<InfraConfig, ApiError> {
  return useQuery<InfraConfig, ApiError>({
    queryKey: ['infra', 'config'],
    queryFn: () => get('/api/v1/infra/config'),
    retry: false,
  });
}

/** Nome do APIM configurado (`grupos.apim.campos.apim_name`). */
export function gatewayName(config?: InfraConfig): string | null {
  const grupo = config?.groups.find((item) => item.name === 'apim');
  const campo = grupo?.fields.find((item) => item.field === 'apim_name');
  return typeof campo?.value === 'string' ? campo.value : null;
}
